import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDataRefresh, formatLastUpdated } from '@/hooks/useDataRefresh';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Search,
  AlertTriangle,
  CheckCircle2,
  Package,
  BarChart3,
  Target,
  Loader2,
  CalendarIcon,
  History,
  TrendingUp,
  Eye,
  Filter,
  RotateCcw,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  PlusCircle,
  Plus,
  ArrowUpDown,
  HelpCircle,
  X,
  Store,
  RefreshCw
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Updated', 'Live', 'Needs Revision'];

const STATUS_COLORS = {
  'Not Started': 'bg-slate-100 text-slate-600 border-slate-200',
  'In Progress': 'bg-blue-100 text-blue-700 border-blue-200',
  'Updated': 'bg-purple-100 text-purple-700 border-purple-200',
  'Live': 'bg-green-100 text-green-700 border-green-200',
  'Needs Revision': 'bg-orange-100 text-orange-700 border-orange-200'
};

const QUALITY_BAND_COLORS = {
  'Excellent': 'bg-green-500',
  'Good': 'bg-blue-500',
  'Needs Improvement': 'bg-yellow-500',
  'Critical': 'bg-red-500'
};

export default function ListingQualityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Persist active tab in URL
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'dashboard';
  });
  
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [availablePlatforms, setAvailablePlatforms] = useState(['Amazon']);
  const [platformIcons, setPlatformIcons] = useState({});
  const [skuData, setSkuData] = useState({}); // {sku_id: {platform_urls: {...}}}
  const [auditsExpanded, setAuditsExpanded] = useState(false); // Upcoming audits collapsed by default
  
  // Dashboard filters
  const [dashboardPlatform, setDashboardPlatform] = useState('all');
  const [dashboardBrand, setDashboardBrand] = useState('all');
  
  // Products tab filters - cascading/dependent (reactive - no apply button needed)
  const [filterPlatform, setFilterPlatform] = useState('Amazon');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterElements, setFilterElements] = useState([]); // Multi-select array
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterQualityScore, setFilterQualityScore] = useState('all'); // Quality score filter
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // Product name sort
  // Resizable Product column — width in px, persisted to localStorage so
  // preference survives reloads. Clamp to [200, 640] to keep the layout sane.
  const [productColWidth, setProductColWidth] = useState(() => {
    const saved = parseInt(typeof window !== 'undefined' ? (localStorage.getItem('pdp-product-col-width') || '') : '', 10);
    return Number.isFinite(saved) && saved >= 200 && saved <= 640 ? saved : 320;
  });
  useEffect(() => {
    try { localStorage.setItem('pdp-product-col-width', String(productColWidth)); } catch {}
  }, [productColWidth]);
  const productColResizingRef = useRef(false);
  const onProductColResizeStart = useCallback((e) => {
    e.preventDefault();
    productColResizingRef.current = true;
    const startX = e.clientX;
    const startW = productColWidth;
    const onMove = (ev) => {
      const next = Math.max(200, Math.min(640, startW + (ev.clientX - startX)));
      setProductColWidth(next);
    };
    const onUp = () => {
      productColResizingRef.current = false;
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [productColWidth]);
  const [showHelp, setShowHelp] = useState(false); // Help modal
  
  // Inline editing state
  const [savingCell, setSavingCell] = useState(null); // {productId, elementKey}
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkElement, setBulkElement] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  // Grouped bulk-actions modal — lets the user apply status + audit dates
  // (next review + last audited) across MULTIPLE elements in one shot for the
  // currently-selected rows.
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkElementsMulti, setBulkElementsMulti] = useState([]); // array of element keys
  const [bulkNextReviewDate, setBulkNextReviewDate] = useState('');
  const [applyingBulk, setApplyingBulk] = useState(false);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const url = new URL(window.location);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url);
  };

  // Get elements for selected platform (reactive - uses filterPlatform directly)
  const platformElements = useMemo(() => {
    if (!config?.platforms || !filterPlatform) return {};
    return config.platforms[filterPlatform] || {};
  }, [config, filterPlatform]);

  // Fetch initial data
  useEffect(() => {
    fetchConfig();
    fetchBrands();
  }, []);

  // Fetch dashboard when dashboard filters change
  useEffect(() => {
    if (config) {
      fetchDashboard();
    }
  }, [dashboardPlatform, dashboardBrand, config]);

  // Fetch products when filters change (reactive)
  useEffect(() => {
    if (config) {
      fetchProducts();
    }
  }, [filterPlatform, filterBrand, config]);

  // Reset dependent filters when platform changes
  useEffect(() => {
    setFilterElements([]);
    setFilterStatus('all');
    setFilterQualityScore('all');
  }, [filterPlatform]);
  
  // Reset filters function
  const handleResetFilters = () => {
    setFilterPlatform('Amazon');
    setFilterBrand('all');
    setFilterElements([]);
    setFilterStatus('all');
    setFilterQualityScore('all');
    setSearchQuery('');
  };

  // Toggle element in multi-select
  const toggleElementFilter = (elementKey) => {
    setFilterElements(prev => 
      prev.includes(elementKey)
        ? prev.filter(e => e !== elementKey)
        : [...prev, elementKey]
    );
  };

  // Reset brand filter when switching to dashboard tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      setDashboardBrand('all');
    }
  }, [activeTab]);

  const fetchConfigMemo = useCallback(async () => {
    try {
      const [configRes, iconsRes, skusRes] = await Promise.all([
        fetch(`${API_URL}/api/listing-quality/config`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platform-icons`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/skus`, { credentials: 'include' })
      ]);
      
      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data);
        if (data.available_platforms && data.available_platforms.length > 0) {
          // Filter out Meta - it's only for Task Submission
          const filteredPlatforms = data.available_platforms.filter(p => p !== 'Meta');
          setAvailablePlatforms(filteredPlatforms);
        }
      }
      
      if (iconsRes.ok) {
        setPlatformIcons(await iconsRes.json());
      }
      
      if (skusRes.ok) {
        const skus = await skusRes.json();
        // Create a lookup map by product_id (which is sku.id)
        const skuMap = {};
        skus.forEach(sku => {
          skuMap[sku.id] = sku;
        });
        setSkuData(skuMap);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  }, []);

  const fetchConfig = fetchConfigMemo;

  // Auto-refresh — DISABLED on window focus. Users complained the whole
  // products table reloaded (fetchConfig → config state change → fetchProducts
  // effect) every time they switched to another browser tab and came back.
  // The listing-quality data isn't so time-sensitive that we need to refetch
  // every tab switch — user can hit the manual refresh button when needed.
  const { refresh, lastUpdated } = useDataRefresh(fetchConfigMemo, {
    refreshOnFocus: false,
    refreshInterval: 0,
    minRefreshGap: 10000
  });

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/api/masters/brands`, {
        credentials: 'include'
      });
      if (response.ok) {
        setBrands(await response.json());
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        platform: filterPlatform,
        sort_by: 'product_name',
        sort_order: 'asc'
      });
      if (filterBrand && filterBrand !== 'all') {
        params.append('brand_id', filterBrand);
      }
      
      const response = await fetch(`${API_URL}/api/listing-quality?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        setProducts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const params = new URLSearchParams({
        platform: dashboardPlatform
      });
      if (dashboardBrand && dashboardBrand !== 'all') {
        params.append('brand_id', dashboardBrand);
      }
      
      const response = await fetch(`${API_URL}/api/listing-quality/dashboard?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        setDashboardStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  // Inline status update
  const handleInlineStatusUpdate = async (productId, elementKey, newStatus) => {
    setSavingCell({ productId, elementKey });
    const timestamp = new Date().toISOString();
    try {
      const response = await fetch(
        `${API_URL}/api/listing-quality/${productId}/${filterPlatform}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            elements: { [elementKey]: { status: newStatus } }
          })
        }
      );
      
      if (response.ok) {
        // Update local state immediately for responsiveness — the local calc
        // below keeps the dashboard summary numbers in sync without a full
        // API round-trip, which used to make the whole page feel like it was
        // reloading after every dropdown change.
        setProducts(prev => prev.map(p => {
          if (p.product_id === productId) {
            const updatedElements = { ...p.elements };
            // Initialize element if it doesn't exist
            if (!updatedElements[elementKey]) {
              updatedElements[elementKey] = {};
            }
            updatedElements[elementKey] = { 
              ...updatedElements[elementKey], 
              status: newStatus,
              last_updated: timestamp
            };
            
            // Recalculate completion and score locally
            const elemKeys = Object.keys(platformElements);
            const liveCount = elemKeys.filter(k => updatedElements[k]?.status === 'Live').length;
            const completion = elemKeys.length > 0 ? Math.round((liveCount / elemKeys.length) * 100) : 0;
            const score = elemKeys.reduce((sum, k) => {
              if (updatedElements[k]?.status === 'Live') {
                return sum + (platformElements[k]?.weight || 0);
              }
              return sum;
            }, 0);
            
            // Determine quality band
            let band = 'Critical';
            if (score >= 85) band = 'Excellent';
            else if (score >= 70) band = 'Good';
            else if (score >= 50) band = 'Needs Improvement';
            
            return {
              ...p,
              elements: updatedElements,
              completion_percentage: completion,
              quality_score: score,
              quality_band: band
            };
          }
          return p;
        }));
        
        // Dashboard summary is a separate tab — it re-fetches automatically
        // via its own useEffect on tab switch or filter change. Calling
        // fetchDashboard() here on every dropdown click was triggering a
        // full-page-feeling re-render for users on production, so we skip it.
        toast.success('Status updated');
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSavingCell(null);
    }
  };

  // Inline Next Audit date update
  const handleInlineAuditDateUpdate = async (productId, elementKey, newDate) => {
    setSavingCell({ productId, elementKey });
    try {
      const response = await fetch(
        `${API_URL}/api/listing-quality/${productId}/${filterPlatform}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            elements: { [elementKey]: { next_review_date: newDate } }
          })
        }
      );
      
      if (response.ok) {
        // Update local state immediately
        setProducts(prev => prev.map(p => {
          if (p.product_id === productId) {
            const updatedElements = { ...p.elements };
            if (updatedElements[elementKey]) {
              updatedElements[elementKey] = { 
                ...updatedElements[elementKey], 
                next_review_date: newDate
              };
            }
            return { ...p, elements: updatedElements };
          }
          return p;
        }));
        
        fetchDashboard(); // Refresh upcoming audits
        toast.success('Audit date updated');
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update audit date');
    } finally {
      setSavingCell(null);
    }
  };

  // Bulk status update for selected products
  const handleBulkStatusUpdate = async () => {
    if (selectedProducts.length === 0 || !bulkElement || !bulkStatus) {
      toast.error('Please select products, element, and status');
      return;
    }
    
    setApplyingBulk(true);
    const timestamp = new Date().toISOString();
    let successCount = 0;
    let failCount = 0;
    
    for (const productId of selectedProducts) {
      try {
        const response = await fetch(
          `${API_URL}/api/listing-quality/${productId}/${filterPlatform}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              elements: { [bulkElement]: { status: bulkStatus } }
            })
          }
        );
        
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }
    
    // Update local state for all successful updates
    setProducts(prev => prev.map(p => {
      if (selectedProducts.includes(p.product_id)) {
        const updatedElements = { ...p.elements };
        if (!updatedElements[bulkElement]) {
          updatedElements[bulkElement] = {};
        }
        updatedElements[bulkElement] = { 
          ...updatedElements[bulkElement], 
          status: bulkStatus,
          last_updated: timestamp
        };
        
        // Recalculate completion and score locally
        const elemKeys = Object.keys(platformElements);
        const liveCount = elemKeys.filter(k => updatedElements[k]?.status === 'Live').length;
        const completion = elemKeys.length > 0 ? Math.round((liveCount / elemKeys.length) * 100) : 0;
        const score = elemKeys.reduce((sum, k) => {
          if (updatedElements[k]?.status === 'Live') {
            return sum + (platformElements[k]?.weight || 0);
          }
          return sum;
        }, 0);
        
        let band = 'Critical';
        if (score >= 85) band = 'Excellent';
        else if (score >= 70) band = 'Good';
        else if (score >= 50) band = 'Needs Improvement';
        
        return {
          ...p,
          elements: updatedElements,
          completion_percentage: completion,
          quality_score: score,
          quality_band: band
        };
      }
      return p;
    }));
    
    // Clear selection and refresh
    setSelectedProducts([]);
    setBulkElement('');
    setBulkStatus('');
    fetchDashboard();
    setApplyingBulk(false);
    
    if (failCount === 0) {
      toast.success(`Updated ${successCount} products`);
    } else {
      toast.warning(`Updated ${successCount} products, ${failCount} failed`);
    }
  };

  // Grouped bulk actions: applies status + next_review_date + last_audited_date
  // to ALL selected products across ALL selected elements. Each product gets
  // one PUT with a compound `elements` map so the DB write is atomic per row.
  const handleBulkActionsApply = async () => {
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }
    if (bulkElementsMulti.length === 0) {
      toast.error('Pick at least one element');
      return;
    }
    if (!bulkStatus && !bulkNextReviewDate) {
      toast.error('Set at least one field (status or next audit date)');
      return;
    }

    // Build the per-element patch — only include fields the user actually filled.
    const perElementPatch = {};
    if (bulkStatus) perElementPatch.status = bulkStatus;
    if (bulkNextReviewDate) perElementPatch.next_review_date = bulkNextReviewDate;

    const elementsPayload = {};
    bulkElementsMulti.forEach(k => { elementsPayload[k] = { ...perElementPatch }; });

    setApplyingBulk(true);
    let successCount = 0;
    let failCount = 0;

    for (const productId of selectedProducts) {
      try {
        const response = await fetch(
          `${API_URL}/api/listing-quality/${productId}/${filterPlatform}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ elements: elementsPayload }),
          }
        );
        if (response.ok) successCount++; else failCount++;
      } catch { failCount++; }
    }

    // Optimistic local patch so the UI reflects the changes immediately.
    setProducts(prev => prev.map(p => {
      if (!selectedProducts.includes(p.product_id)) return p;
      const updatedElements = { ...(p.elements || {}) };
      bulkElementsMulti.forEach(k => {
        updatedElements[k] = { ...(updatedElements[k] || {}), ...perElementPatch };
      });
      return { ...p, elements: updatedElements };
    }));

    setSelectedProducts([]);
    setBulkElementsMulti([]);
    setBulkStatus('');
    setBulkNextReviewDate('');
    setShowBulkActions(false);
    setApplyingBulk(false);
    fetchDashboard();

    const cellCount = successCount * bulkElementsMulti.length;
    if (failCount === 0) {
      toast.success(`Updated ${cellCount} cells across ${successCount} products`);
    } else {
      toast.warning(`Updated ${cellCount} cells (${failCount} products failed)`);
    }
  };


  // Toggle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Select/Deselect all visible products
  const toggleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.product_id));
    }
  };

  // Filter products reactively (no apply button needed)
  const filteredProducts = useMemo(() => {
    let result = products;
    
    // Search filter (reactive)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.product_name?.toLowerCase().includes(query) ||
        p.brand_name?.toLowerCase().includes(query)
      );
    }
    
    // Element filter (multi-select) - show only products that have ALL selected elements
    if (filterElements && filterElements.length > 0) {
      result = result.filter(p => 
        filterElements.every(elem => p.elements?.[elem])
      );
    }
    
    // Status filter - show products where selected elements have this status
    if (filterStatus && filterStatus !== 'all') {
      if (filterElements && filterElements.length > 0) {
        // Filter by selected elements' status
        result = result.filter(p => 
          filterElements.some(elem => p.elements?.[elem]?.status === filterStatus)
        );
      } else {
        // Filter by any element having this status
        result = result.filter(p => 
          Object.values(p.elements || {}).some(e => e.status === filterStatus)
        );
      }
    }
    
    // Quality Score filter
    if (filterQualityScore && filterQualityScore !== 'all') {
      result = result.filter(p => p.quality_band === filterQualityScore);
    }
    
    // Sort by product name
    result = [...result].sort((a, b) => {
      const nameA = (a.product_name || '').toLowerCase();
      const nameB = (b.product_name || '').toLowerCase();
      return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
    
    return result;
  }, [products, searchQuery, filterElements, filterStatus, filterQualityScore, sortOrder]);

  const goToProducts = () => {
    setFilterPlatform(dashboardPlatform);
    setFilterBrand(dashboardBrand);
    setFilterElements([]);
    setFilterQualityScore('all');
    setFilterStatus('all');
    setSearchQuery('');
    setActiveTab('products');
  };

  // Get platform URL for a product
  const getProductPlatformUrl = (productId, platform) => {
    const sku = skuData[productId];
    if (sku?.platform_urls?.[platform]) {
      return sku.platform_urls[platform];
    }
    return null;
  };

  // Get brand store URL for a platform
  const getBrandStoreUrl = (brandId, platform) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.store_urls?.[platform] || null;
  };

  // Create task for element - navigate to task submission with pre-filled data
  const handleCreateTask = (product, elementKey, elementName) => {
    // Find brand info
    const brand = brands.find(b => b.id === product.brand_id);
    
    // Store pre-fill data in sessionStorage
    const prefillData = {
      brand_id: product.brand_id,
      brand_name: brand?.name || product.brand_name,
      platform: filterPlatform,
      element_name: elementName,
      element_key: elementKey,
      product_id: product.product_id,
      product_name: product.product_name
    };
    
    sessionStorage.setItem('taskPrefill', JSON.stringify(prefillData));
    navigate('/submit');
  };

  // Render platform icon
  const PlatformIcon = ({ platform, size = 16 }) => {
    const iconUrl = platformIcons[platform];
    if (iconUrl) {
      return (
        <img 
          src={iconUrl} 
          alt={platform} 
          className="object-contain"
          style={{ width: size, height: size }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }
    return null;
  };

  return (
    <div className="animate-fade-in" data-testid="listing-quality-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2EB4AC] to-[#20A39E] flex items-center justify-center shadow-lg shadow-[#2EB4AC]/25">
              <Target className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">PDP Checker</h1>
              <p className="text-sm text-slate-500">Monitor and manage product listing quality</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-slate-400">
                Updated {formatLastUpdated(lastUpdated)}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              className="text-slate-600"
              data-testid="refresh-pdp-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHelp(true)}
              className="gap-2"
            >
              <HelpCircle className="h-4 w-4" />
              Help
            </Button>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-slate-50">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-[#2EB4AC]" />
                How to Use PRISM
              </h2>
              <button onClick={() => setShowHelp(false)} className="p-1 hover:bg-slate-200 rounded-lg">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[calc(80vh-60px)] space-y-5 text-sm">
              <div>
                <h3 className="font-semibold text-[#2EB4AC] mb-2">📊 PDP Checker - Dashboard</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li>View overall quality scores and distribution across products</li>
                  <li>Filter by Platform and Brand to narrow down results</li>
                  <li>Upcoming Audits shows elements due for review - click <strong>Take Action</strong> to jump to that product</li>
                  <li>Click <strong>Expand/Collapse</strong> to show/hide audit details</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-[#2EB4AC] mb-2">📦 PDP Checker - Products Tab</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li><strong>Platform/Brand Bars:</strong> Click icons to filter products</li>
                  <li><strong>Filters:</strong> Use Elements, Status, Quality Score, and Search to find specific products</li>
                  <li><strong>Sort:</strong> Click the sort button on Product column to sort A-Z or Z-A</li>
                  <li><strong>Inline Edit:</strong> Change status directly from the dropdown in each cell</li>
                  <li><strong>Set Audit Date:</strong> Click the calendar button to schedule next review</li>
                  <li><strong>Create Task:</strong> Click the + icon next to any element to create a design task</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-[#2EB4AC] mb-2">📝 Task Manager</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li><strong>Dashboard:</strong> Overview of all tasks with status distribution</li>
                  <li><strong>Submit Task:</strong> Create new design requests with brand, platform, and requirements</li>
                  <li><strong>Task Log:</strong> View, filter, and manage all submitted tasks</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-[#2EB4AC] mb-2">⚙️ Master Data (Settings)</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li><strong>Brands:</strong> Add/edit brands with icons</li>
                  <li><strong>Platforms:</strong> Configure e-commerce platforms with icons</li>
                  <li><strong>Products (SKUs):</strong> Manage products with platform-specific URLs</li>
                  <li><strong>Task Types:</strong> Define design task categories</li>
                  <li><strong>CSV Import/Export:</strong> Bulk upload products using templates</li>
                </ul>
              </div>
              <div className="pt-3 border-t">
                <p className="text-slate-500 text-xs">💡 Tip: Use keyboard shortcuts - Press Enter in search to quickly filter results</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="products" className="data-[state=active]:bg-white">
            <Package className="h-4 w-4 mr-2" />
            Products
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Platform:</Label>
              <Select value={dashboardPlatform} onValueChange={setDashboardPlatform}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {availablePlatforms.map(platform => (
                    <SelectItem key={platform} value={platform}>{platform}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Brand:</Label>
              <Select value={dashboardBrand} onValueChange={setDashboardBrand}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map(brand => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" size="sm" onClick={goToProducts} className="ml-auto">
              <Eye className="h-4 w-4 mr-2" />
              View Products
            </Button>
          </div>

          {dashboardStats && (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card 
                  className="bg-white border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleTabChange('products')}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Total Products</p>
                        <p className="text-3xl font-bold text-slate-900">{dashboardStats.total_products}</p>
                        <p className="text-xs text-[#2EB4AC] mt-1">Click to view all →</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                        <Package className="h-6 w-6 text-slate-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Avg Quality Score</p>
                        <p className="text-3xl font-bold text-slate-900">{dashboardStats.average_score}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-[#2EB4AC]/10 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-[#2EB4AC]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Action Required</p>
                        <p className="text-3xl font-bold text-orange-600">{dashboardStats.action_required_count}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                        <AlertTriangle className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Excellent Rating</p>
                        <p className="text-3xl font-bold text-green-600">{dashboardStats.band_distribution?.Excellent || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quality Band Distribution */}
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Quality Distribution</CardTitle>
                  <CardDescription>Products by quality band for {dashboardPlatform}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    {['Excellent', 'Good', 'Needs Improvement', 'Critical'].map(band => (
                      <div key={band} className="text-center p-4 rounded-xl bg-slate-50">
                        <div className={cn("w-4 h-4 rounded-full mx-auto mb-2", QUALITY_BAND_COLORS[band])} />
                        <p className="text-2xl font-bold text-slate-900">
                          {dashboardStats.band_distribution?.[band] || 0}
                        </p>
                        <p className="text-xs text-slate-500">{band}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Audits */}
              <Card className={cn(
                "bg-white border-0 shadow-sm transition-all",
                !auditsExpanded && dashboardStats.upcoming_reviews?.length > 0 && "ring-2 ring-[#2EB4AC] ring-offset-2 animate-pulse"
              )}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-[#2EB4AC]" />
                        Upcoming Audits
                        {dashboardStats.upcoming_reviews?.length > 0 && (
                          <Badge className="bg-[#2EB4AC] text-white text-xs ml-2">
                            {dashboardStats.upcoming_reviews.length}
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>Elements scheduled for audit</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAuditsExpanded(!auditsExpanded)}
                      className={cn(
                        "h-9 px-3 font-medium transition-all",
                        auditsExpanded 
                          ? "bg-slate-100 hover:bg-slate-200 text-slate-600" 
                          : "bg-[#2EB4AC] hover:bg-[#25908A] text-white border-[#2EB4AC]"
                      )}
                    >
                      {auditsExpanded ? (
                        <>
                          <ChevronUp className="h-4 w-4 mr-1" />
                          Collapse
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-1" />
                          Expand
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                {auditsExpanded && (
                  <CardContent>
                    <div className="space-y-3">
                      {dashboardStats.upcoming_reviews?.length > 0 ? (
                        dashboardStats.upcoming_reviews.map((item, idx) => (
                          <div 
                            key={`${item.product_id}-${item.element_key}-${idx}`} 
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg transition-all hover:scale-[1.01] hover:shadow-md cursor-pointer",
                              item.is_overdue 
                                ? "bg-red-50 border border-red-200" 
                                : item.is_urgent 
                                  ? "bg-orange-50 border border-orange-200"
                                  : "bg-slate-50 hover:bg-slate-100"
                            )}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              {/* Product Image */}
                              <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden border border-slate-200">
                                {item.image_url ? (
                                  <img 
                                    src={item.image_url} 
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <Package className="h-4 w-4 text-slate-400" />
                                )}
                              </div>
                              <div className={cn(
                                "w-2 h-2 rounded-full flex-shrink-0",
                                item.is_overdue ? "bg-red-500" : item.is_urgent ? "bg-orange-500" : "bg-[#2EB4AC]"
                              )} />
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900 text-sm truncate">{item.product_name}</p>
                                <p className="text-xs text-slate-500">{item.brand_name} • {item.element_key.replace(/_/g, ' ')}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className={cn(
                                "text-sm font-medium",
                                item.is_overdue ? "text-red-600" : item.is_urgent ? "text-orange-600" : "text-slate-600"
                              )}>
                                {item.is_overdue 
                                  ? `${Math.abs(item.days_until)} days overdue`
                                  : item.days_until === 0 
                                    ? "Due today"
                                    : `${item.days_until} days`
                                }
                              </p>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setFilterPlatform(dashboardPlatform);
                                  setFilterBrand(item.brand_id || 'all');
                                  setSearchQuery(item.product_name || '');
                                  setActiveTab('products');
                                }}
                                className={cn(
                                  "h-7 px-3 text-xs font-medium",
                                  item.is_overdue 
                                    ? "bg-red-500 hover:bg-red-600 text-white"
                                    : item.is_urgent
                                      ? "bg-orange-500 hover:bg-orange-600 text-white"
                                      : "bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                                )}
                              >
                                Take Action
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <CalendarIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                          <p className="text-sm text-slate-500">No upcoming audits scheduled</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        {/* Products Tab - Table View */}
        <TabsContent value="products" className="space-y-4">
          {/* Platform & Brand Icon Bars */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4 space-y-4">
              {/* Platform Bar */}
              <div>
                <Label className="text-xs text-slate-500 font-medium mb-2 block">Platform</Label>
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {availablePlatforms.map(platform => (
                    <button
                      key={platform}
                      onClick={() => setFilterPlatform(platform)}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all flex-shrink-0",
                        filterPlatform === platform 
                          ? "bg-[#2EB4AC]/10 border-[#2EB4AC] text-[#2EB4AC] shadow-sm" 
                          : "bg-slate-50 border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-100"
                      )}
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        {platformIcons[platform] ? (
                          <img 
                            src={platformIcons[platform]} 
                            alt={platform} 
                            className="w-6 h-6 object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <span className="text-sm font-bold">{platform.charAt(0)}</span>
                        )}
                      </div>
                      <span className="text-sm font-medium">{platform}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Brand Bar */}
              <div>
                <Label className="text-xs text-slate-500 font-medium mb-2 block">Brand</Label>
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  <button
                    onClick={() => setFilterBrand('all')}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all flex-shrink-0",
                      filterBrand === 'all'
                        ? "bg-[#2EB4AC]/10 border-[#2EB4AC] text-[#2EB4AC] shadow-sm"
                        : "bg-slate-50 border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-100"
                    )}
                  >
                    <span className="text-sm font-medium">All Brands</span>
                  </button>
                  {brands.map(brand => {
                    const isSelected = filterBrand === brand.id;
                    const storeUrl = brand.store_urls?.[filterPlatform];
                    
                    return (
                      <div key={brand.id} className="flex flex-col items-center flex-shrink-0">
                        <button
                          onClick={() => setFilterBrand(brand.id)}
                          className={cn(
                            "flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all",
                            isSelected
                              ? "bg-[#2EB4AC]/10 border-[#2EB4AC] text-[#2EB4AC] shadow-sm"
                              : "bg-slate-50 border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-100"
                          )}
                        >
                          <div className="w-6 h-6 flex items-center justify-center">
                            {brand.icon_url ? (
                              <img src={brand.icon_url} alt="" className="w-6 h-6 object-contain" onError={(e) => e.target.style.display='none'} />
                            ) : (
                              <span className="w-6 h-6 rounded-lg bg-slate-200 flex items-center justify-center text-xs font-bold">{brand.name.charAt(0)}</span>
                            )}
                          </div>
                          <span className="text-sm font-medium">{brand.name}</span>
                        </button>
                        {/* Store link directly below selected brand card */}
                        {isSelected && storeUrl && (
                          <a
                            href={storeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 flex items-center gap-1 text-[10px] text-purple-600 hover:text-purple-700 hover:underline transition-colors"
                            data-testid="brand-store-link"
                          >
                            <Store className="h-3 w-3" />
                            View Store
                            <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Responsive Cascading Filters */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Filters</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleResetFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" />
                  Reset
                </Button>
              </div>
              
              {/* Responsive Filter Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Element Filter - Multi-select Dropdown */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500 font-medium">Elements</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full h-9 justify-between font-normal">
                        <span className="truncate">
                          {filterElements.length === 0 
                            ? 'All Elements' 
                            : filterElements.length === 1
                              ? platformElements[filterElements[0]]?.name || filterElements[0]
                              : `${filterElements.length} selected`
                          }
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56">
                      {Object.entries(platformElements).map(([key, elem]) => (
                        <DropdownMenuCheckboxItem
                          key={key}
                          checked={filterElements.includes(key)}
                          onCheckedChange={() => toggleElementFilter(key)}
                        >
                          {elem.name}
                        </DropdownMenuCheckboxItem>
                      ))}
                      {filterElements.length > 0 && (
                        <div className="border-t mt-1 pt-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full text-xs text-slate-500"
                            onClick={() => setFilterElements([])}
                          >
                            Clear Selection
                          </Button>
                        </div>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Status Filter */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500 font-medium">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {STATUS_OPTIONS.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quality Score Filter */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500 font-medium">Quality Score</Label>
                  <Select value={filterQualityScore} onValueChange={setFilterQualityScore}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Scores</SelectItem>
                      <SelectItem value="Excellent">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          Excellent (85-100)
                        </span>
                      </SelectItem>
                      <SelectItem value="Good">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          Good (70-84)
                        </span>
                      </SelectItem>
                      <SelectItem value="Needs Improvement">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                          Needs Improvement (50-69)
                        </span>
                      </SelectItem>
                      <SelectItem value="Critical">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          Critical (&lt;50)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Search */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500 font-medium">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-9"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Products Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2EB4AC]" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No products found</h3>
                <p className="text-slate-500 mb-4">
                  {products.length === 0 
                    ? "No products have URLs for this platform. Add platform URLs in Settings → Products." 
                    : "No products match your filters"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-0 shadow-sm overflow-hidden">
              {/* Bulk Action Bar - Shows when products are selected */}
              {selectedProducts.length > 0 && (
                <div className="px-4 py-3 bg-[#2EB4AC]/10 border-b border-[#2EB4AC]/20 flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedProducts.length === filteredProducts.length}
                      onCheckedChange={toggleSelectAll}
                      className="border-[#2EB4AC] data-[state=checked]:bg-[#2EB4AC]"
                    />
                    <span className="text-sm font-medium text-[#2EB4AC]">
                      {selectedProducts.length} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedProducts([])}
                      className="text-slate-500 hover:text-slate-700 h-7 px-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => setShowBulkActions(true)}
                    className="h-8 bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                    data-testid="pdp-bulk-actions-btn"
                  >
                    Bulk Actions ({selectedProducts.length})
                  </Button>
                </div>
              )}
              
              {/* Product Count Header */}
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-white">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-600">Products</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Showing</span>
                    <Badge 
                      className="bg-[#2EB4AC] text-white animate-pulse font-semibold"
                      data-testid="product-count-badge"
                    >
                      {filteredProducts.length}
                    </Badge>
                    <span className="text-xs text-slate-500">of {products.length} products</span>
                  </div>
              </div>
              <div className="overflow-auto max-h-[60vh]">
                <table className="w-full caption-bottom text-sm">
                  <thead className="sticky top-0 z-20 bg-slate-100 shadow-sm">
                    <tr className="border-b-2 border-slate-200">
                      {/* Checkbox Column Header */}
                      <th className="h-12 w-10 px-2 text-center align-middle bg-slate-100 sticky left-0 z-30">
                        <Checkbox
                          checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                          onCheckedChange={toggleSelectAll}
                          className="border-slate-400"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th
                        className="h-12 px-3 text-left align-middle font-semibold text-slate-700 sticky left-10 bg-slate-100 z-30 relative"
                        style={{ width: `${productColWidth}px`, minWidth: `${productColWidth}px`, maxWidth: `${productColWidth}px` }}
                      >
                        <button 
                          onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                          className="flex items-center gap-1.5 hover:text-[#2EB4AC] transition-colors"
                        >
                          Product
                          <ArrowUpDown className="h-3.5 w-3.5" />
                          <span className="text-[10px] text-slate-400 font-normal">({sortOrder === 'asc' ? 'A-Z' : 'Z-A'})</span>
                        </button>
                        {/* Resize handle — a subtle 1 px divider that thickens
                            and tints teal on hover. Grip dots appear only when
                            hovering to keep the header uncluttered at rest. */}
                        <div
                          onMouseDown={onProductColResizeStart}
                          onDoubleClick={() => setProductColWidth(320)}
                          className="absolute top-2 bottom-2 right-0 w-1 cursor-col-resize bg-slate-200 hover:bg-[#2EB4AC] hover:w-1.5 transition-all z-40 rounded-full"
                          title="Drag to resize · double-click to reset"
                          data-testid="pdp-product-col-resizer"
                        />
                      </th>
                      {Object.entries(platformElements).map(([key, elem]) => (
                        <th key={key} className="h-12 px-2 text-center align-middle font-semibold text-slate-700 min-w-[180px] bg-slate-100">
                          <div className="flex flex-col items-center">
                            <span className="text-xs">{elem.name}</span>
                            <span className="text-[10px] text-slate-400 font-normal">Weight: {elem.weight}%</span>
                          </div>
                        </th>
                      ))}
                      <th className="h-12 px-2 text-center align-middle font-semibold text-slate-700 min-w-[90px] bg-slate-100">Completion</th>
                      <th className="h-12 px-2 text-center align-middle font-semibold text-slate-700 min-w-[80px] bg-slate-100">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => {
                      const platformUrl = getProductPlatformUrl(product.product_id, filterPlatform);
                      const isSelected = selectedProducts.includes(product.product_id);
                      
                      return (
                      <tr key={product.product_id} className={cn(
                        "border-b hover:bg-slate-50 group",
                        isSelected && "bg-[#2EB4AC]/5"
                      )}>
                        {/* Checkbox Column */}
                        <td className={cn(
                          "w-10 px-2 align-middle text-center sticky left-0 z-20",
                          // Sticky columns need SOLID backgrounds so scrolled
                          // cells don't bleed through. `bg-[#2EB4AC]/5` (5%
                          // opacity) was letting content leak through at zoom
                          // levels other than 100%. Use full-opacity teal
                          // tint on selected rows, plain white otherwise.
                          isSelected ? "bg-[#F0FAF9] group-hover:bg-[#E4F5F3]" : "bg-white group-hover:bg-slate-50"
                        )}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleProductSelection(product.product_id)}
                            className="border-slate-400 data-[state=checked]:bg-[#2EB4AC] data-[state=checked]:border-[#2EB4AC]"
                            data-testid={`select-product-${product.product_id}`}
                          />
                        </td>
                        <td
                          className={cn(
                            "p-2 align-middle sticky left-10 z-20",
                            // z-20 (was z-10) so scrolled body cells slide
                            // *behind* this column — fixes the visual glitch
                            // where element cells peeked through on zoom.
                            // Solid opaque background (no /5 opacity) is
                            // critical here, otherwise scrolling cells bleed
                            // through at non-100% zoom levels.
                            isSelected ? "bg-[#F0FAF9] group-hover:bg-[#E4F5F3]" : "bg-white group-hover:bg-slate-50"
                          )}
                          style={{ width: `${productColWidth}px`, minWidth: `${productColWidth}px`, maxWidth: `${productColWidth}px` }}
                        >
                          <div className="flex items-center gap-3">
                            {/* Product Image */}
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200 overflow-hidden">
                              {product.image_url ? (
                                <img 
                                  src={product.image_url} 
                                  alt={product.product_name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { 
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full items-center justify-center ${product.image_url ? 'hidden' : 'flex'}`}>
                                <Package className="h-5 w-5 text-slate-400" />
                              </div>
                            </div>
                            {/* Platform Icon */}
                            <div className="flex-shrink-0 w-6 h-6 rounded bg-slate-50 flex items-center justify-center border border-slate-100">
                              <PlatformIcon platform={filterPlatform} size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                              {platformUrl ? (
                                <a 
                                  href={platformUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-[#2EB4AC] hover:underline flex items-center gap-1.5 group/link"
                                >
                                  <span className="truncate">{product.product_name}</span>
                                  <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
                                </a>
                              ) : (
                                <p className="font-medium text-slate-900 truncate">
                                  {product.product_name}
                                </p>
                              )}
                              <div className="flex items-center gap-1.5">
                                <p className="text-xs text-slate-500">{product.brand_name}</p>
                                {product.product_type && product.product_type !== 'Single' && (
                                  <Badge 
                                    variant="secondary" 
                                    className={cn(
                                      "text-[9px] px-1 py-0 h-3.5",
                                      product.product_type === 'Combo' 
                                        ? 'bg-amber-100 text-amber-700 border-amber-200' 
                                        : 'bg-purple-100 text-purple-700 border-purple-200'
                                    )}
                                  >
                                    {product.product_type}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        {Object.entries(platformElements).map(([key, elem]) => {
                          const elementData = product.elements?.[key];
                          const status = elementData?.status || 'Not Started';
                          const lastUpdated = elementData?.last_updated;
                          const nextAudit = elementData?.next_review_date;
                          const isSaving = savingCell?.productId === product.product_id && savingCell?.elementKey === key;
                          
                          return (
                            <td key={key} className="text-center p-2 align-middle">
                              <div className="flex flex-col items-center gap-1.5">
                                {/* Status Dropdown with Create Task */}
                                <div className="flex items-center gap-1 w-full max-w-[160px]">
                                  <Select
                                    value={status}
                                    onValueChange={(newStatus) => handleInlineStatusUpdate(product.product_id, key, newStatus)}
                                    disabled={isSaving}
                                  >
                                    <SelectTrigger 
                                      className={cn(
                                        "h-7 text-xs border flex-1",
                                        STATUS_COLORS[status],
                                        isSaving && "opacity-50"
                                      )}
                                    >
                                      {isSaving ? (
                                        <Loader2 className="h-3 w-3 animate-spin mx-auto" />
                                      ) : (
                                        <SelectValue />
                                      )}
                                    </SelectTrigger>
                                    <SelectContent>
                                      {STATUS_OPTIONS.map(s => (
                                        <SelectItem key={s} value={s}>
                                          <span className={cn("px-2 py-0.5 rounded text-xs", STATUS_COLORS[s])}>
                                            {s}
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={() => handleCreateTask(product, key, elem.name)}
                                          className="p-1 rounded hover:bg-[#2EB4AC]/10 text-slate-400 hover:text-[#2EB4AC] transition-colors"
                                        >
                                          <PlusCircle className="h-4 w-4" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <p className="text-xs">Create task for {elem.name}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                                
                                {/* Next Audit Date Picker */}
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className={cn(
                                        "h-6 px-2 text-[10px] w-full max-w-[140px] justify-start",
                                        nextAudit && new Date(nextAudit) < new Date() 
                                          ? "text-red-500 bg-red-50 hover:bg-red-100" 
                                          : "text-slate-500 hover:bg-slate-100"
                                      )}
                                      disabled={isSaving}
                                    >
                                      <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                      {nextAudit 
                                        ? format(new Date(nextAudit), 'MMM d, yyyy')
                                        : 'Set Audit Date'}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="center">
                                    <Calendar
                                      mode="single"
                                      selected={nextAudit ? new Date(nextAudit) : undefined}
                                      onSelect={(date) => handleInlineAuditDateUpdate(product.product_id, key, date?.toISOString() || null)}
                                      initialFocus
                                    />
                                    {nextAudit && (
                                      <div className="p-2 border-t">
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                                          onClick={() => handleInlineAuditDateUpdate(product.product_id, key, null)}
                                        >
                                          Clear Date
                                        </Button>
                                      </div>
                                    )}
                                  </PopoverContent>
                                </Popover>
                                
                                {/* Last Updated Info */}
                                {lastUpdated && (
                                  <div 
                                    className="flex items-center justify-center gap-1 text-[10px] text-slate-400"
                                    title={`Last Updated: ${format(new Date(lastUpdated), 'MMM d, yyyy HH:mm')}`}
                                  >
                                    <History className="h-2.5 w-2.5" />
                                    <span>{format(new Date(lastUpdated), 'MMM d')}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td className="p-2 align-middle">
                          <div className="flex items-center justify-center">
                            {/* Circle Progress */}
                            <div className="relative w-12 h-12">
                              <svg className="w-12 h-12 transform -rotate-90">
                                <circle
                                  cx="24"
                                  cy="24"
                                  r="20"
                                  stroke="#e2e8f0"
                                  strokeWidth="4"
                                  fill="none"
                                />
                                <circle
                                  cx="24"
                                  cy="24"
                                  r="20"
                                  stroke="#2EB4AC"
                                  strokeWidth="4"
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeDasharray={`${(product.completion_percentage || 0) * 1.256} 125.6`}
                                />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-slate-700">
                                {product.completion_percentage || 0}%
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="p-2 align-middle text-center">
                          <div className={cn(
                            "inline-flex items-center justify-center w-10 h-10 rounded-lg text-white font-bold text-sm",
                            QUALITY_BAND_COLORS[product.quality_band || 'Critical']
                          )}>
                            {product.quality_score || 0}
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Bulk Actions modal — set status + audit dates across selected products
          for one or more elements in a single write. */}
      {showBulkActions && (
        <Dialog open onOpenChange={setShowBulkActions}>
          <DialogContent className="max-w-lg" data-testid="pdp-bulk-modal">
            <DialogHeader>
              <DialogTitle>Bulk Actions · {selectedProducts.length} products</DialogTitle>
              <DialogDescription>
                Apply status and/or next audit date to one or more elements on every selected product. Blank fields are left unchanged.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">
                  Elements to update ({bulkElementsMulti.length} selected)
                </label>
                <div className="max-h-48 overflow-auto border border-slate-200 rounded-md p-2 space-y-1 bg-slate-50">
                  {Object.keys(platformElements).length === 0 && (
                    <p className="text-xs text-slate-500 p-2">No elements configured for this platform.</p>
                  )}
                  {Object.entries(platformElements).map(([key, elem]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-2 py-1"
                      data-testid={`pdp-bulk-elem-${key}`}>
                      <Checkbox
                        checked={bulkElementsMulti.includes(key)}
                        onCheckedChange={(v) => {
                          setBulkElementsMulti(prev =>
                            v ? [...prev, key] : prev.filter(k => k !== key)
                          );
                        }}
                      />
                      <span className="text-sm text-slate-700">{elem.name}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => setBulkElementsMulti(Object.keys(platformElements))}
                    className="text-xs text-[#2EB4AC] hover:underline"
                    data-testid="pdp-bulk-elem-all">
                    Select all
                  </button>
                  <button type="button" onClick={() => setBulkElementsMulti([])}
                    className="text-xs text-slate-500 hover:underline"
                    data-testid="pdp-bulk-elem-none">
                    Clear
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Status (optional)</label>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger className="h-9" data-testid="pdp-bulk-status">
                    <SelectValue placeholder="— leave unchanged —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— leave unchanged —</SelectItem>
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s} value={s}>
                        <span className={cn("px-2 py-0.5 rounded text-xs", STATUS_COLORS[s])}>{s}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">
                  Next audit date (optional)
                </label>
                <Input
                  type="date"
                  value={bulkNextReviewDate}
                  onChange={e => setBulkNextReviewDate(e.target.value)}
                  className="h-9 w-full"
                  data-testid="pdp-bulk-next-review"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkActions(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  // Treat sentinel "__none__" as blank so the payload skips status.
                  if (bulkStatus === '__none__') setBulkStatus('');
                  setTimeout(handleBulkActionsApply, 0);
                }}
                disabled={applyingBulk || bulkElementsMulti.length === 0
                  || (!bulkStatus && !bulkNextReviewDate)}
                className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                data-testid="pdp-bulk-apply-btn"
              >
                {applyingBulk && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
